import type { UploadedFile } from '@backmarket/nuxt-module-upload/api/upload-service/types'
import { useFileUpload } from '@backmarket/nuxt-module-upload/useFileUpload'

import type { ReportIllicitContentAttachment } from '~/api/care-platform/types/HelpCenterForms.types'

import {
  EVIDENCE_UPLOAD,
  MAX_EVIDENCE_FILE_SIZE,
} from '../ReportIllicitContent.constants'

const RESOURCE_TYPES = ['image', 'video', 'raw'] as const

function isResourceType(
  value: string,
): value is ReportIllicitContentAttachment['resourceType'] {
  return (RESOURCE_TYPES as readonly string[]).includes(value)
}

// Uploads the reporter's evidence files to UaaS v2 and returns the attachment
// handles the help center form endpoint expects. Call it in a setup context:
// `useFileUpload` registers the shared use case config with `useAsyncData`.
export function useReportIllicitContentEvidenceUpload() {
  const { upload } = useFileUpload({
    ...EVIDENCE_UPLOAD,
    maxFileSize: MAX_EVIDENCE_FILE_SIZE,
  })

  // A failed submission is retried with the same files: do not upload them
  // twice. Weak keys: an unselected file is released with its `File` object.
  const uploadedFiles = new WeakMap<File, UploadedFile>()

  async function uploadFile(
    file: File,
  ): Promise<ReportIllicitContentAttachment> {
    let uploaded = uploadedFiles.get(file)

    if (!uploaded) {
      uploaded = await upload(file)
      uploadedFiles.set(file, uploaded)
    }

    if (!isResourceType(uploaded.resourceType)) {
      throw new Error(`Unexpected resource type: ${uploaded.resourceType}`)
    }

    return {
      publicId: uploaded.publicId,
      resourceType: uploaded.resourceType,
      ...(uploaded.format !== undefined && { format: uploaded.format }),
    }
  }

  // Rejects with the first failure, after every file settled so successful
  // uploads stay cached for the retry.
  async function uploadEvidence(files: File[]) {
    const results = await Promise.allSettled(files.map(uploadFile))
    const failure = results.find(
      (result): result is PromiseRejectedResult => result.status === 'rejected',
    )

    if (failure) throw failure.reason

    return results.map(
      (result) =>
        (result as PromiseFulfilledResult<ReportIllicitContentAttachment>)
          .value,
    )
  }

  return { uploadEvidence }
}
